<template>
  <div class="wrapper trainer-theme" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <!-- Componente Sidebar -->
    <trainer-sidebar 
      :collapsed="sidebarCollapsed"
      :mobile-open="mobileSidebarOpen"
      @logout="logout"
      @item-click="closeMobileSidebar"
    />
    
    <!-- Mobile Overlay -->
    <div class="mobile-overlay" 
         v-show="mobileSidebarOpen" 
         @click="closeMobileSidebar">
    </div>

    <div class="main-panel trainer-main-panel">
      <!-- Componente Header -->
      <trainer-header 
        :sidebar-collapsed="sidebarCollapsed"
        @toggle-sidebar="toggleSidebar"
        @logout="logout"
      />

      <!-- Contenido principal -->
      <div class="content trainer-content">
        <router-view></router-view>
      </div>

      <!-- Footer simplificado -->
      <footer class="footer trainer-footer">
        <div class="container-fluid">
          <div class="copyright float-right">
            &copy; {{ new Date().getFullYear() }} 
            <a href="#">Central Gym</a> - Personal Trainer DEMO
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import TrainerSidebar from './TrainerSidebar.vue';
import TrainerHeader from './TrainerHeader.vue';
import swipeDetector from 'src/mixins/swipeDetector';

export default {
  name: 'TrainerLayout',
  mixins: [swipeDetector],
  components: {
    TrainerSidebar,
    TrainerHeader
  },
  data() {
    return {
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      isMobile: false
    }
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    
    // Auto-cerrar en móvil al iniciar
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }
    
    // Configurar estado inicial basado en preferencias del usuario
    const savedSidebarState = localStorage.getItem('trainer-sidebar-collapsed');
    if (savedSidebarState !== null && !this.isMobile) {
      this.sidebarCollapsed = JSON.parse(savedSidebarState);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    toggleSidebar() {
      if (this.isMobile) {
        this.mobileSidebarOpen = !this.mobileSidebarOpen;
      } else {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        // Guardar preferencia del usuario
        localStorage.setItem('trainer-sidebar-collapsed', JSON.stringify(this.sidebarCollapsed));
      }
    },
    closeMobileSidebar() {
      if (this.isMobile) {
        this.mobileSidebarOpen = false;
      }
    },
    // Sobrescribir métodos del mixin para TrainerLayout
    onSwipeRight() {
      // Abrir sidebar solo en móvil
      if (window.innerWidth <= 991) {
        this.mobileSidebarOpen = true;
      }
    },
    onSwipeLeft() {
      // Cerrar sidebar solo en móvil
      if (window.innerWidth <= 991) {
        this.mobileSidebarOpen = false;
      }
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 991;
      if (this.isMobile) {
        this.sidebarCollapsed = true;
        this.mobileSidebarOpen = false;
      }
    },
    logout() {
      this.$swal.fire({
        title: '¿Cerrar Sesión?',
        text: 'Serás redirigido al login principal del sistema',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, cerrar sesión',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#9ACD32',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          // Limpiar todos los datos de sesión
          sessionStorage.clear();
          localStorage.clear();
          
          // Limpiar cookies relacionadas con la sesión
          document.cookie.split(";").forEach(function(c) { 
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
          });
          
          // Mostrar mensaje de confirmación
          this.$swal.fire({
            title: '¡Sesión Cerrada!',
            text: 'Has salido del modo Personal Trainer exitosamente',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            // Redirigir al login principal
            this.$router.push('/login').catch(() => {
              // Recargar la página como fallback si hay problemas con la navegación
              window.location.href = '/login';
            });
          });
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
/* Paleta de colores basada en el logo (verde lima) */
.trainer-theme {
  --trainer-primary: #9ACD32;    /* Verde lima principal */
  --trainer-secondary: #7CB342;  /* Verde más oscuro */
  --trainer-accent: #8BC34A;     /* Verde intermedio */
  --trainer-dark: #689F38;       /* Verde oscuro */
  --trainer-light: #DCEDC8;      /* Verde muy claro */
  --trainer-bg: #F1F8E9;         /* Fondo muy suave */
}

.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  z-index: 999;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.trainer-main-panel {
  position: relative;
  float: right;
  width: calc(100% - 260px);
  min-height: 100vh;
  background: var(--trainer-bg);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);

  .trainer-content {
    padding: 30px 15px;
    min-height: calc(100vh - 123px);
    margin-top: 50px;
  }

  .trainer-footer {
    border-top: 1px solid #e7e7e7;
    background: #fafafa;
    padding: 15px 0;
    
    .copyright {
      color: var(--trainer-secondary);
      font-size: 14px;
      
      a {
        color: var(--trainer-primary);
        text-decoration: none;
        
        &:hover {
          color: var(--trainer-dark);
        }
      }
    }
  }
}

/* Estados de colapso */
.sidebar-collapsed {
  .trainer-main-panel {
    width: calc(100% - 70px);
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
}

/* Responsive */
@media (max-width: 991px) {
  .trainer-main-panel {
    width: 100% !important;
  }
  
  .sidebar-collapsed .trainer-main-panel {
    width: 100% !important;
  }
}
</style>